import React, { useEffect, useMemo, useState } from "react";
import API from "../../../services/api";
import SaveStudioAssetButton from "../SaveStudioAssetButton";
import FieldHint from "../FieldHint";
import useI18n from "../../../i18n/useI18n";

import MotionSurface from "../../../motion/studio/MotionSurface";
import ToolResultSkeleton from "../../loaders/ToolResultSkeleton";

import ResultCard from "../../ui/ResultCard";
import ActionRow from "../../ui/ActionRow";
import CopyButton from "../../ui/CopyButton";

import AIActionButton from "../../ui/buttons/AIActionButton";
import UtilityButton from "../../ui/buttons/UtilityButton";

const THEME_KEY = "hookora_theme";

const platformOptions = [
  { value: "instagram", key: "instagram", fallback: "Instagram" },
  { value: "tiktok", key: "tiktok", fallback: "TikTok" },
  { value: "youtube", key: "youtube", fallback: "YouTube" },
  { value: "x", key: "x", fallback: "X / Twitter" },
  { value: "facebook", key: "facebook", fallback: "Facebook" },
  { value: "linkedin", key: "linkedin", fallback: "LinkedIn" },
  { value: "general", key: "general", fallback: "General" },
];

const contentTypeOptions = [
  { value: "hooks", key: "hooks", fallback: "Hooks" },
  { value: "captions", key: "captions", fallback: "Captions" },
  { value: "scripts", key: "scripts", fallback: "Scripts" },
  { value: "carousels", key: "carousels", fallback: "Carousels" },
  { value: "ads", key: "ads", fallback: "Ads" },
  { value: "general", key: "generalContent", fallback: "General Content" },
];

const getStoredTheme = () => {
  if (typeof window === "undefined") return "light";
  return localStorage.getItem(THEME_KEY) || "light";
};

const normalizeArray = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string") return item.trim();
        if (item && typeof item === "object") {
          return (
            item.text ||
            item.content ||
            item.label ||
            item.title ||
            item.name ||
            JSON.stringify(item)
          );
        }
        return String(item ?? "").trim();
      })
      .filter(Boolean);
  }

  if (typeof value === "string") {
    return value
      .split("\n")
      .map((line) => line.replace(/^[-*•\d.)\s]+/, "").trim())
      .filter(Boolean);
  }

  return [];
};

const formatResultToText = (result, t) => {
  if (!result) return "";
  if (typeof result === "string") return result;

  const sections = [];

  const coreAngle =
    result.core_angle ||
    result.differentiation_angle ||
    result.positioning ||
    result.output ||
    result.result;

  if (coreAngle) {
    sections.push(
      `${t("studioTools.contentDifferentiationTool.resultLabels.differentiationAngle")}:\n${coreAngle}`
    );
  }

  const summary = result.summary || result.overview || result.analysis || result.explanation;
  if (summary) {
    sections.push(`${t("studioTools.contentDifferentiationTool.resultLabels.summary")}:\n${summary}`);
  }

  const gaps = normalizeArray(result.market_gaps || result.gaps || result.white_space);
  if (gaps.length) {
    sections.push(
      `${t("studioTools.contentDifferentiationTool.resultLabels.marketGaps")}:\n${gaps
        .map((item) => `- ${item}`)
        .join("\n")}`
    );
  }

  const strengths = normalizeArray(
    result.unique_strengths || result.strengths || result.advantages
  );
  if (strengths.length) {
    sections.push(
      `${t("studioTools.contentDifferentiationTool.resultLabels.uniqueStrengths")}:\n${strengths
        .map((item) => `- ${item}`)
        .join("\n")}`
    );
  }

  const ideas = normalizeArray(
    result.differentiation_ideas ||
      result.content_ideas ||
      result.ideas ||
      result.positioning_ideas
  );
  if (ideas.length) {
    sections.push(
      `${t("studioTools.contentDifferentiationTool.resultLabels.differentiationIdeas")}:\n${ideas
        .map((item) => `- ${item}`)
        .join("\n")}`
    );
  }

  const warnings = normalizeArray(result.warnings || result.avoid || result.common_traps);
  if (warnings.length) {
    sections.push(
      `${t("studioTools.contentDifferentiationTool.resultLabels.avoid")}:\n${warnings
        .map((item) => `- ${item}`)
        .join("\n")}`
    );
  }

  const sampleOutput =
    result.sample_output || result.example || result.example_content || result.rewrite_example;
  if (sampleOutput) {
    sections.push(
      `${t("studioTools.contentDifferentiationTool.resultLabels.sampleOutput")}:\n${sampleOutput}`
    );
  }

  if (!sections.length) {
    return JSON.stringify(result, null, 2);
  }

  return sections.join("\n\n");
};

const ContentDifferentiationTool = ({
  sharedTopic,
  setSharedTopic,
  sharedPlatform,
  setSharedPlatform,
}) => {
  const { t } = useI18n();

  const [theme, setTheme] = useState(getStoredTheme());
  const [brandContext, setBrandContext] = useState("");
  const [competitorStyle, setCompetitorStyle] = useState("");
  const [audience, setAudience] = useState("");
  const [goal, setGoal] = useState("");
  const [contentType, setContentType] = useState("general");
  const [currentContent, setCurrentContent] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const selectedPlatform = sharedPlatform || "general";
  const topicValue = sharedTopic || "";

  useEffect(() => {
    const syncTheme = () => setTheme(getStoredTheme());

    syncTheme();
    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-theme-change", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-theme-change", syncTheme);
    };
  }, []);

  const isDark = theme === "dark";

  const styles = useMemo(() => {
    return {
      wrapper: {
        display: "flex",
        flexDirection: "column",
        gap: 18,
        color: isDark ? "#f8fafc" : "#0f172a",
      },
      card: {
        background: isDark
          ? "linear-gradient(180deg, rgba(15,23,42,0.96) 0%, rgba(15,23,42,0.90) 100%)"
          : "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
        border: `1px solid ${isDark ? "rgba(148,163,184,0.16)" : "rgba(15,23,42,0.08)"}`,
        borderRadius: 20,
        padding: 20,
        boxShadow: isDark
          ? "0 10px 30px rgba(0,0,0,0.22)"
          : "0 10px 30px rgba(15,23,42,0.06)",
      },
      headerRow: {
        display: "flex",
        justifyContent: "space-between",
        gap: 16,
        alignItems: "flex-start",
        flexWrap: "wrap",
      },
      titleWrap: {
        display: "flex",
        flexDirection: "column",
        gap: 8,
        maxWidth: 780,
      },
      badge: {
        alignSelf: "flex-start",
        padding: "6px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        background: isDark ? "rgba(16,185,129,0.16)" : "rgba(16,185,129,0.10)",
        color: isDark ? "#6ee7b7" : "#047857",
        border: `1px solid ${isDark ? "rgba(16,185,129,0.24)" : "rgba(16,185,129,0.18)"}`,
      },
      title: {
        margin: 0,
        fontSize: 22,
        fontWeight: 800,
        letterSpacing: "-0.02em",
      },
      subtitle: {
        margin: 0,
        fontSize: 14,
        lineHeight: 1.6,
        color: isDark ? "rgba(226,232,240,0.78)" : "rgba(15,23,42,0.68)",
      },
      formGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
        gap: 16,
        marginTop: 18,
      },
      fieldWrap: {
        display: "flex",
        flexDirection: "column",
        gap: 8,
      },
      fullWidthField: {
        display: "flex",
        flexDirection: "column",
        gap: 8,
        gridColumn: "1 / -1",
      },
      label: {
        fontSize: 13,
        fontWeight: 700,
        color: isDark ? "#e2e8f0" : "#0f172a",
      },
      input: {
        width: "100%",
        borderRadius: 14,
        border: `1px solid ${isDark ? "rgba(148,163,184,0.18)" : "rgba(15,23,42,0.12)"}`,
        background: isDark ? "rgba(15,23,42,0.72)" : "#ffffff",
        color: isDark ? "#f8fafc" : "#0f172a",
        padding: "13px 14px",
        outline: "none",
        fontSize: 14,
        boxSizing: "border-box",
      },
      textarea: {
        width: "100%",
        minHeight: 140,
        resize: "vertical",
        borderRadius: 14,
        border: `1px solid ${isDark ? "rgba(148,163,184,0.18)" : "rgba(15,23,42,0.12)"}`,
        background: isDark ? "rgba(15,23,42,0.72)" : "#ffffff",
        color: isDark ? "#f8fafc" : "#0f172a",
        padding: 14,
        outline: "none",
        fontSize: 14,
        lineHeight: 1.55,
        boxSizing: "border-box",
      },
      actionsRow: {
        display: "flex",
        gap: 12,
        flexWrap: "wrap",
        marginTop: 18,
      },
      primaryButton: {
        border: "none",
        borderRadius: 14,
        padding: "12px 18px",
        fontSize: 14,
        fontWeight: 800,
        cursor: loading ? "not-allowed" : "pointer",
        background: loading
          ? isDark
            ? "rgba(16,185,129,0.45)"
            : "rgba(16,185,129,0.45)"
          : isDark
          ? "linear-gradient(135deg, #10b981 0%, #0ea5e9 100%)"
          : "linear-gradient(135deg, #059669 0%, #0284c7 100%)",
        color: "#ffffff",
        boxShadow: loading
          ? "none"
          : isDark
          ? "0 12px 24px rgba(16,185,129,0.20)"
          : "0 12px 24px rgba(5,150,105,0.16)",
      },
      secondaryButton: {
        borderRadius: 14,
        padding: "12px 18px",
        fontSize: 14,
        fontWeight: 700,
        cursor: "pointer",
        border: `1px solid ${isDark ? "rgba(148,163,184,0.18)" : "rgba(15,23,42,0.12)"}`,
        background: isDark ? "rgba(30,41,59,0.82)" : "#ffffff",
        color: isDark ? "#e2e8f0" : "#0f172a",
      },
      mutedText: {
        fontSize: 13,
        color: isDark ? "rgba(226,232,240,0.68)" : "rgba(15,23,42,0.62)",
      },
      errorBox: {
        marginTop: 16,
        padding: 14,
        borderRadius: 14,
        background: isDark ? "rgba(127,29,29,0.25)" : "rgba(254,226,226,1)",
        border: `1px solid ${isDark ? "rgba(248,113,113,0.22)" : "rgba(248,113,113,0.28)"}`,
        color: isDark ? "#fecaca" : "#991b1b",
        fontSize: 14,
        lineHeight: 1.5,
      },
      resultCard: {
        background: isDark ? "rgba(2,6,23,0.56)" : "rgba(248,250,252,0.96)",
        border: `1px solid ${isDark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)"}`,
        borderRadius: 18,
        padding: 18,
        display: "flex",
        flexDirection: "column",
        gap: 16,
      },
      resultHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
      },
      resultTitle: {
        margin: 0,
        fontSize: 18,
        fontWeight: 800,
      },
      resultActions: {
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
        alignItems: "center",
      },
      section: {
        display: "flex",
        flexDirection: "column",
        gap: 10,
      },
      sectionTitle: {
        margin: 0,
        fontSize: 14,
        fontWeight: 800,
        color: isDark ? "#f1f5f9" : "#0f172a",
      },
      paragraph: {
        margin: 0,
        fontSize: 14,
        lineHeight: 1.75,
        color: isDark ? "rgba(226,232,240,0.86)" : "rgba(15,23,42,0.82)",
        whiteSpace: "pre-wrap",
      },
      list: {
        margin: 0,
        paddingLeft: 18,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        color: isDark ? "rgba(226,232,240,0.86)" : "rgba(15,23,42,0.82)",
        fontSize: 14,
        lineHeight: 1.65,
      },
      chipRow: {
        display: "flex",
        flexWrap: "wrap",
        gap: 10,
      },
      chip: {
        padding: "7px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        background: isDark ? "rgba(51,65,85,0.88)" : "#ffffff",
        color: isDark ? "#cbd5e1" : "#334155",
        border: `1px solid ${isDark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)"}`,
      },
      outputBox: {
        borderRadius: 16,
        padding: 16,
        background: isDark ? "rgba(15,23,42,0.85)" : "#ffffff",
        border: `1px solid ${isDark ? "rgba(148,163,184,0.12)" : "rgba(15,23,42,0.08)"}`,
      },
    };
  }, [isDark, loading]);

  const formattedText = useMemo(() => formatResultToText(result, t), [result, t]);

  const coreAngle =
    result?.core_angle ||
    result?.differentiation_angle ||
    result?.positioning ||
    result?.output ||
    result?.result ||
    "";

  const gaps = useMemo(
    () => normalizeArray(result?.market_gaps || result?.gaps || result?.white_space),
    [result]
  );

  const strengths = useMemo(
    () => normalizeArray(result?.unique_strengths || result?.strengths || result?.advantages),
    [result]
  );

  const ideas = useMemo(
    () =>
      normalizeArray(
        result?.differentiation_ideas ||
          result?.content_ideas ||
          result?.ideas ||
          result?.positioning_ideas
      ),
    [result]
  );

  const warnings = useMemo(
    () => normalizeArray(result?.warnings || result?.avoid || result?.common_traps),
    [result]
  );

  const clearResults = () => {
    setResult(null);
    setError("");
    setCopied(false);
  };

  const resetAll = () => {
    setBrandContext("");
    setCompetitorStyle("");
    setAudience("");
    setGoal("");
    setContentType("general");
    setCurrentContent("");
    setResult(null);
    setCopied(false);
    setError("");
    if (typeof setSharedTopic === "function") setSharedTopic("");
    if (typeof setSharedPlatform === "function") setSharedPlatform("general");
  };

  const copyResult = async () => {
    if (!formattedText) return;

    try {
      await navigator.clipboard.writeText(formattedText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setError(t("studioTools.contentDifferentiationTool.messages.copyFailed"));
    }
  };

  const tryPost = async (paths, payload) => {
    let lastError = null;

    for (const path of paths) {
      try {
        const response = await API.post(path, payload);
        return response?.data;
      } catch (err) {
        lastError = err;
      }
    }

    throw lastError;
  };

  const handleGenerate = async () => {
    if (!topicValue.trim() && !brandContext.trim()) {
      setError(t("studioTools.contentDifferentiationTool.messages.topicOrContextRequired"));
      return;
    }

    setLoading(true);
    setError("");
    setCopied(false);

    const payload = {
      topic: topicValue.trim(),
      platform: selectedPlatform,
      content_type: contentType,
      brand_context: brandContext.trim(),
      competitor_style: competitorStyle.trim(),
      target_audience: audience.trim(),
      goal: goal.trim(),
      current_content: currentContent.trim(),
    };

    try {
      const data = await tryPost(
        [
          "/studio/content-differentiation",
          "/studio/content_differentiation",
          "/studio/differentiation",
          "/studio/differentiation-content",
        ],
        payload
      );

      const normalized =
        data?.result ||
        data?.differentiation ||
        data?.data ||
        data?.content_differentiation ||
        data;

      setResult(normalized);
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        t("studioTools.contentDifferentiationTool.messages.generateFailed");
      setError(message);
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <MotionSurface>
    <div style={styles.wrapper}>
      <div style={styles.card}>
        <div style={styles.headerRow}>
          <div style={styles.titleWrap}>
            <span style={styles.badge}>{t("studioTools.contentDifferentiationTool.badge")}</span>
            <h2 style={styles.title}>{t("studioTools.contentDifferentiationTool.title")}</h2>
            <p style={styles.subtitle}>{t("studioTools.contentDifferentiationTool.description")}</p>
          </div>
        </div>

        <div style={styles.formGrid}>
          <div style={styles.fieldWrap}>
            <label style={styles.label}>{t("studioTools.contentDifferentiationTool.fields.topic.label")}</label>
            <input
              type="text"
              value={topicValue}
              onChange={(e) => setSharedTopic?.(e.target.value)}
              placeholder={t("studioTools.contentDifferentiationTool.fields.topic.placeholder")}
              style={styles.input}
            />
            <FieldHint text={t("studioTools.contentDifferentiationTool.fields.topic.hint")} />
          </div>

          <div style={styles.fieldWrap}>
            <label style={styles.label}>
              {t("studioTools.contentDifferentiationTool.fields.platform.label")}
            </label>
            <select
              value={selectedPlatform}
              onChange={(e) => setSharedPlatform?.(e.target.value)}
              style={styles.input}
            >
              {platformOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(`studioTools.contentDifferentiationTool.platformOptions.${option.key}`, option.fallback)}
                </option>
              ))}
            </select>
            <FieldHint text={t("studioTools.contentDifferentiationTool.fields.platform.hint")} />
          </div>

          <div style={styles.fieldWrap}>
            <label style={styles.label}>
              {t("studioTools.contentDifferentiationTool.fields.contentType.label")}
            </label>
            <select
              value={contentType}
              onChange={(e) => setContentType(e.target.value)}
              style={styles.input}
            >
              {contentTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(
                    `studioTools.contentDifferentiationTool.contentTypeOptions.${option.key}`,
                    option.fallback
                  )}
                </option>
              ))}
            </select>
            <FieldHint text={t("studioTools.contentDifferentiationTool.fields.contentType.hint")} />
          </div>

          <div style={styles.fieldWrap}>
            <label style={styles.label}>
              {t("studioTools.contentDifferentiationTool.fields.targetAudience.label")}
            </label>
            <input
              type="text"
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              placeholder={t("studioTools.contentDifferentiationTool.fields.targetAudience.placeholder")}
              style={styles.input}
            />
            <FieldHint text={t("studioTools.contentDifferentiationTool.fields.targetAudience.hint")} />
          </div>

          <div style={styles.fieldWrap}>
            <label style={styles.label}>{t("studioTools.contentDifferentiationTool.fields.goal.label")}</label>
            <input
              type="text"
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              placeholder={t("studioTools.contentDifferentiationTool.fields.goal.placeholder")}
              style={styles.input}
            />
            <FieldHint text={t("studioTools.contentDifferentiationTool.fields.goal.hint")} />
          </div>

          <div style={styles.fullWidthField}>
            <label style={styles.label}>
              {t("studioTools.contentDifferentiationTool.fields.brandContext.label")}
            </label>
            <textarea
              value={brandContext}
              onChange={(e) => setBrandContext(e.target.value)}
              placeholder={t("studioTools.contentDifferentiationTool.fields.brandContext.placeholder")}
              style={styles.textarea}
            />
            <FieldHint text={t("studioTools.contentDifferentiationTool.fields.brandContext.hint")} />
          </div>

          <div style={styles.fullWidthField}>
            <label style={styles.label}>
              {t("studioTools.contentDifferentiationTool.fields.competitorStyle.label")}
            </label>
            <textarea
              value={competitorStyle}
              onChange={(e) => setCompetitorStyle(e.target.value)}
              placeholder={t("studioTools.contentDifferentiationTool.fields.competitorStyle.placeholder")}
              style={styles.textarea}
            />
            <FieldHint text={t("studioTools.contentDifferentiationTool.fields.competitorStyle.hint")} />
          </div>

          <div style={styles.fullWidthField}>
            <label style={styles.label}>
              {t("studioTools.contentDifferentiationTool.fields.currentContent.label")}
            </label>
            <textarea
              value={currentContent}
              onChange={(e) => setCurrentContent(e.target.value)}
              placeholder={t("studioTools.contentDifferentiationTool.fields.currentContent.placeholder")}
              style={styles.textarea}
            />
            <FieldHint text={t("studioTools.contentDifferentiationTool.fields.currentContent.hint")} />
          </div>
        </div>

        <ActionRow>
          <AIActionButton isDark={isDark}
            type="button"
            onClick={handleGenerate}
            disabled={loading}
          >
            {loading
              ? t("studioTools.contentDifferentiationTool.actions.generating")
              : t("studioTools.contentDifferentiationTool.actions.generate")}
          </AIActionButton>

          <UtilityButton isDark={isDark}
            type="button"
            onClick={clearResults}
          >
            {t("studioTools.contentDifferentiationTool.actions.clearResults")}
          </UtilityButton>

          <UtilityButton isDark={isDark}
            type="button"
            onClick={resetAll}
          >
            {t("studioTools.contentDifferentiationTool.actions.reset")}
          </UtilityButton>
        </ActionRow>

        <div style={{ marginTop: 10 }}>
          <span style={styles.mutedText}>{t("studioTools.contentDifferentiationTool.tip")}</span>
        </div>

        {error ? <div style={styles.errorBox}>{error}</div> : null}
      </div>

      {loading ? (
        <ToolResultSkeleton isDark={isDark} count={3} />
      ) : result ? (
        <div style={styles.card}>
          <ResultCard
            colors={{
              border: isDark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)",
              cardBgStrong: isDark ? "rgba(2,6,23,0.56)" : "rgba(248,250,252,0.96)",
              shadowSoft: isDark
                ? "0 10px 24px rgba(2,6,23,0.22)"
                : "0 6px 18px rgba(15,23,42,0.05)",
            }}
            index={0}
          >
            <div style={styles.resultHeader}>
              <h3 style={styles.resultTitle}>{t("studioTools.contentDifferentiationTool.results.title")}</h3>

              <ActionRow style={styles.resultActions}>
                <CopyButton
                  type="button"
                  copied={copied}
                  onClick={copyResult}
                >
                  {copied
                    ? t("studioTools.contentDifferentiationTool.actions.copied")
                    : t("studioTools.contentDifferentiationTool.actions.copy")}
                </CopyButton>

                <SaveStudioAssetButton
                  toolType="content_differentiation"
                  title={`${t("studioTools.contentDifferentiationTool.badge")}${topicValue ? ` - ${topicValue}` : ""}`}
                  topic={topicValue || t("studioTools.contentDifferentiationTool.badge")}
                  platform={selectedPlatform}
                  content={formattedText}
                  meta={{
                    brandContext,
                    competitorStyle,
                    audience,
                    goal,
                    contentType,
                    currentContent,
                  }}
                />
              </ActionRow>
            </div>

            <div style={styles.chipRow}>
              <span style={styles.chip}>
                {t(
                  `studioTools.contentDifferentiationTool.platformOptions.${
                    platformOptions.find((item) => item.value === selectedPlatform)?.key || "general"
                  }`,
                  "General"
                )}
              </span>
              <span style={styles.chip}>
                {t(
                  `studioTools.contentDifferentiationTool.contentTypeOptions.${
                    contentTypeOptions.find((item) => item.value === contentType)?.key || "generalContent"
                  }`,
                  "General Content"
                )}
              </span>
              {topicValue ? <span style={styles.chip}>{topicValue}</span> : null}
            </div>

            {coreAngle ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>
                  {t("studioTools.contentDifferentiationTool.resultLabels.differentiationAngle")}
                </h4>
                <div style={styles.outputBox}>
                  <p style={styles.paragraph}>{coreAngle}</p>
                </div>
              </div>
            ) : null}

            {result?.summary || result?.overview || result?.analysis || result?.explanation ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>
                  {t("studioTools.contentDifferentiationTool.resultLabels.summary")}
                </h4>
                <p style={styles.paragraph}>
                  {result?.summary || result?.overview || result?.analysis || result?.explanation}
                </p>
              </div>
            ) : null}

            {gaps.length ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>
                  {t("studioTools.contentDifferentiationTool.resultLabels.marketGaps")}
                </h4>
                <ul style={styles.list}>
                  {gaps.map((item, index) => (
                    <li key={`gap-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {strengths.length ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>
                  {t("studioTools.contentDifferentiationTool.resultLabels.uniqueStrengths")}
                </h4>
                <ul style={styles.list}>
                  {strengths.map((item, index) => (
                    <li key={`strength-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {ideas.length ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>
                  {t("studioTools.contentDifferentiationTool.resultLabels.differentiationIdeas")}
                </h4>
                <ul style={styles.list}>
                  {ideas.map((item, index) => (
                    <li key={`idea-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {warnings.length ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>
                  {t("studioTools.contentDifferentiationTool.resultLabels.avoid")}
                </h4>
                <ul style={styles.list}>
                  {warnings.map((item, index) => (
                    <li key={`warning-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {result?.sample_output ||
            result?.example ||
            result?.example_content ||
            result?.rewrite_example ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>
                  {t("studioTools.contentDifferentiationTool.resultLabels.sampleOutput")}
                </h4>
                <div style={styles.outputBox}>
                  <p style={styles.paragraph}>
                    {result?.sample_output ||
                      result?.example ||
                      result?.example_content ||
                      result?.rewrite_example}
                  </p>
                </div>
              </div>
            ) : null}

            {!formattedText ? (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>
                  {t("studioTools.contentDifferentiationTool.resultLabels.rawResult")}
                </h4>
                <p style={styles.paragraph}>{JSON.stringify(result, null, 2)}</p>
              </div>
            ) : null}
          </ResultCard>
        </div>
      ) : null}
    </div>
    </MotionSurface>
  );
};

export default ContentDifferentiationTool;